import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { enhancedApi, mapApiErrorsToForm } from '../api';
import {
  Button,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  Autocomplete,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { validators, runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { fieldHelperText } from '../utils/formHelpers';

const TASK_TITLE_MAX = 200;
const DESCRIPTION_MAX = 2000;

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'completed', label: 'Complete' },
];

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low', color: 'default' },
  { value: 'Medium', label: 'Medium', color: 'info' },
  { value: 'High', label: 'High', color: 'warning' },
  { value: 'Urgent', label: 'Urgent', color: 'error' },
];

const taskSchema = {
  title: [validators.required, validators.minLength(3), validators.maxLength(TASK_TITLE_MAX)],
  description: [validators.maxLength(DESCRIPTION_MAX)],
  estimated_hours: [validators.minValue(0), validators.maxValue(24)],
};

const TaskForm = ({ clientId, onFormSubmit, onClose, initialData = {}, mode = 'create', taskId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [priority, setPriority] = useState('Low');
  const [status, setStatus] = useState('not_started');

  const {
    errors,
    touched,
    validateField,
    touchField,
    validateAll,
    clearErrors,
    mergeServerFieldErrors,
  } = useFormValidation(taskSchema);

  const [staffOptions, setStaffOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formValues = useMemo(
    () => ({
      title: name,
      description,
      estimated_hours: estimatedHours,
    }),
    [name, description, estimatedHours]
  );

  const allFieldsValid = useMemo(() => {
    return !Object.keys(taskSchema).some((key) => runValidators(formValues[key], taskSchema[key]));
  }, [formValues]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await enhancedApi.getUsers();
        const userList = Array.isArray(res.data) ? res.data : (res.data?.users || res.data?.data || []);
        if (userList.length === 0 && clientId) {
          const assignedRes = await api.get(`/api/multi-tenant/clients/${clientId}/assigned-users/`);
          const assigned = assignedRes.data?.assigned_users || [];
          setStaffOptions(
            assigned.map((u) => ({
              id: u.id,
              label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
              username: u.username,
            }))
          );
        } else {
          setStaffOptions(
            userList.map((u) => ({
              id: u.id,
              label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
              username: u.username,
            }))
          );
        }
      } catch {
        setStaffOptions([]);
      }
    };
    fetchUsers();
  }, [clientId]);

  useEffect(() => {
    if (mode === 'edit' && initialData && Object.keys(initialData).length > 0) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      if (initialData.assignee || initialData.assignee_ref) {
        const u = initialData.assignee_ref || { id: initialData.assignee, username: initialData.assignee };
        const label = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`;
        setAssignedTo({ id: u.id, label, username: u.username });
      } else {
        setAssignedTo(null);
      }
      setDueDate(initialData.due_date ? dayjs(initialData.due_date) : null);
      setEstimatedHours(
        initialData.budgeted_hours != null ? String(initialData.budgeted_hours) : ''
      );
      setPriority(initialData.priority || 'Low');
      const st = initialData.status || 'not_started';
      setStatus(STATUS_OPTIONS.some((o) => o.value === st) ? st : 'not_started');
    }
  }, [mode, initialData]);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setAssignedTo(null);
    setDueDate(null);
    setEstimatedHours('');
    setPriority('Low');
    setStatus('not_started');
    clearErrors();
  }, [clearErrors]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const buildPayload = useCallback(() => {
    const hours =
      estimatedHours !== '' && !isNaN(Number(estimatedHours))
        ? Math.min(24, Math.max(0, Number(estimatedHours)))
        : 0;
    return {
      name: name.trim(),
      description: (description || '').trim() || undefined,
      assignee: assignedTo?.username || assignedTo?.label || '',
      due_date: dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : null,
      budgeted_hours: hours,
      priority: priority || 'Low',
      status: status || 'not_started',
      client: clientId || undefined,
    };
  }, [name, description, assignedTo, dueDate, estimatedHours, priority, status, clientId]);

  const handleSubmit = async () => {
    if (!validateAll(formValues)) return;

    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (mode === 'edit' && taskId) {
        await api.patch(`/api/tasks/${taskId}/`, payload);
        setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
      } else {
        await api.post('/api/tasks/', payload);
        setSnackbar({ open: true, message: 'Task created', severity: 'success' });
      }
      clearErrors();
      onFormSubmit();
      resetForm();
      setTimeout(() => onClose(), 400);
    } catch (err) {
      const mapped = mapApiErrorsToForm(err.response?.data);
      const remap = { ...mapped };
      if (remap.name) {
        remap.title = remap.name;
        delete remap.name;
      }
      if (Object.keys(remap).length) mergeServerFieldErrors(remap);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        (typeof err.response?.data === 'object' && err.response?.data
          ? JSON.stringify(err.response.data)
          : null) ||
        err.message ||
        'Failed to save task';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : 'Failed to save task', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const titleErr = touched.title && errors.title;
  const descErr = touched.description && errors.description;
  const hoursErr = touched.estimated_hours && errors.estimated_hours;

  return (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 2,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {mode === 'edit' ? 'Update Task' : 'Add Task'}
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Task Name"
            required
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              if (touched.title) validateField('title', v);
            }}
            onBlur={() => touchField('title', name)}
            error={Boolean(titleErr)}
            helperText={fieldHelperText(titleErr, TASK_TITLE_MAX, name.length)}
            disabled={submitting}
            inputProps={{ maxLength: TASK_TITLE_MAX }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => {
              const v = e.target.value.slice(0, DESCRIPTION_MAX);
              setDescription(v);
              if (touched.description) validateField('description', v);
            }}
            onBlur={() => touchField('description', description)}
            error={Boolean(descErr)}
            helperText={fieldHelperText(descErr, DESCRIPTION_MAX, description.length)}
            disabled={submitting}
            multiline
            rows={3}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
          />

          <Autocomplete
            options={staffOptions}
            getOptionLabel={(opt) => opt?.label ?? ''}
            value={assignedTo}
            onChange={(_, v) => setAssignedTo(v)}
            disabled={submitting}
            renderInput={(params) => (
              <TextField {...params} label="Assigned To" placeholder="Select staff" />
            )}
            isOptionEqualToValue={(a, b) => (a?.id ?? a) === (b?.id ?? b)}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={(v) => setDueDate(v)}
              slotProps={{
                textField: { fullWidth: true, disabled: submitting },
              }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Estimated Hours"
            type="number"
            value={estimatedHours}
            onChange={(e) => {
              const v = e.target.value;
              setEstimatedHours(v);
              if (touched.estimated_hours) validateField('estimated_hours', v);
            }}
            onBlur={() => touchField('estimated_hours', estimatedHours)}
            error={Boolean(hoursErr)}
            helperText={hoursErr || undefined}
            disabled={submitting}
            inputProps={{ min: 0, max: 24, step: 0.5 }}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Priority
            </Typography>
            <ToggleButtonGroup
              value={priority}
              exclusive
              onChange={(_, v) => v != null && setPriority(v)}
              disabled={submitting}
              fullWidth
              size="small"
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  sx={(theme) =>
                    opt.color === 'default'
                      ? {}
                      : {
                          '&.Mui-selected': {
                            color: theme.palette[opt.color]?.main,
                            borderColor: theme.palette[opt.color]?.main,
                          },
                        }
                  }
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <FormControl fullWidth disabled={submitting}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          py: 2,
          px: 3,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!allFieldsValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
        >
          {submitting ? 'Saving...' : mode === 'edit' ? 'Update Task' : 'Create Task'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 6000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TaskForm;
